import type { Vocation } from '@game/protocol';

/**
 * Cliente do Login Server.
 *
 * Fala JSON sobre HTTP, e não o protocolo binário: são conversas de natureza diferente.
 * Aqui há formulário, mensagem de erro em português e latência de rede aceitável em
 * centenas de milissegundos; no jogo há orçamento de milissegundos por tick. Misturar
 * os dois obrigaria a inventar um pacote binário para "senha incorreta".
 */

export interface CharacterSummary {
  id: number;
  name: string;
  vocation: Vocation;
  level: number;
}

export interface LoginResult {
  accountToken: string;
  characters: CharacterSummary[];
  /**
   * Esta conta administra o servidor.
   *
   * Serve para o painel decidir o que **desenhar**, e nada mais: quem autoriza é o servidor,
   * que relê a permissão do banco a cada rota `/api/admin/*`. Um `true` forjado aqui abre
   * uma tela cujos botões respondem 403.
   */
  admin?: boolean;
}

/** Erro com a mensagem que o servidor mandou, pronta para a tela. */
export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

const BASE_URL: string =
  (import.meta.env['VITE_LOGIN_URL'] as string | undefined) ?? 'http://localhost:8081';

async function post<T>(path: string, body: unknown): Promise<T> {
  let response: Response;

  try {
    response = await fetch(`${BASE_URL}${path}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    // Falha de rede não tem status: distinguir "servidor fora do ar" de "senha errada"
    // é a diferença entre o jogador tentar de novo e o jogador desistir.
    throw new ApiError(0, 'não foi possível falar com o servidor');
  }

  const payload = (await response.json().catch(() => ({}))) as Record<string, unknown>;

  if (!response.ok) {
    const message =
      typeof payload['error'] === 'string' ? payload['error'] : 'erro inesperado';
    throw new ApiError(response.status, message);
  }

  return payload as T;
}

export function register(email: string, password: string): Promise<{ accountId: number }> {
  return post('/api/register', { email, password });
}

export function login(email: string, password: string): Promise<LoginResult> {
  return post('/api/login', { email, password });
}

export function createCharacter(
  token: string,
  name: string,
  vocation: number,
): Promise<{ character: CharacterSummary }> {
  return post('/api/characters', { token, name, vocation });
}

/** Token de uso único para o primeiro frame do WebSocket. */
export function requestPlaySession(
  token: string,
  characterId: number,
): Promise<{
  sessionToken: string;
  /**
   * Porta em que este personagem entra, quando o servidor roda **um processo por região**
   * (PRD §3.4).
   *
   * Ausente no modelo de gateway único, que é o padrão: ali existe uma porta só, e o
   * cliente usa a que já conhece. É o "Login Server redireciona o cliente para o endereço
   * correto" de §3.4, e ele vem junto com a sessão porque é a mesma pergunta — "como eu
   * entro?" — e um segundo pedido só para respondê-la seria uma ida e volta a mais no
   * caminho mais sensível da experiência.
   */
  gamePort?: number;
}> {
  return post('/api/play', { token, characterId });
}

// --- Painel administrativo ---------------------------------------------------
//
// Mora aqui, e não num módulo próprio do painel, porque é o mesmo servidor e o mesmo
// contrato: `post` já resolve URL base, erro de rede e mensagem em português, e uma segunda
// cópia dele para o painel divergiria da primeira no primeiro ajuste feito só num dos dois.
// O bundle do jogo não cresce por isso — o painel é outra entrada, e o que ele não importa
// não entra nela.

export interface AdminCharacterRow extends CharacterSummary {
  accountId: number;
  accountEmail: string;
  /** Em jogo agora. O servidor recusa editar quem está online. */
  online: boolean;
}

export interface AdminSkill {
  skill: number;
  name: string;
  level: number;
  tries: number;
  /** Tentativas que o próximo nível exige, nesta vocação. */
  triesForNext: number;
}

export interface AdminCharacter {
  id: number;
  name: string;
  vocation: Vocation;
  vocationName: string;
  level: number;
  experience: number;
  experienceForNextLevel: number;
  health: number;
  maxHealth: number;
  mana: number;
  maxMana: number;
  bankGold: number;
  foodMs: number;
  maxFoodMs: number;
  staminaMs: number;
  maxStaminaMs: number;
  regionId: number;
  skills: AdminSkill[];
  online: boolean;
}

/** O que o formulário manda. Campo ausente é campo não tocado. */
export interface AdminStatusChanges {
  level?: number;
  experience?: number;
  health?: number;
  mana?: number;
  bankGold?: number;
  foodMs?: number;
  staminaMs?: number;
  skills?: Array<{ skill: number; level: number; tries: number }>;
}

export function adminSearch(
  token: string,
  query: string,
): Promise<{ characters: AdminCharacterRow[] }> {
  return post('/api/admin/characters', { token, query });
}

export function adminCharacter(
  token: string,
  characterId: number,
): Promise<{ character: AdminCharacter }> {
  return post('/api/admin/character', { token, characterId });
}

/**
 * Grava e devolve a ficha **como ela ficou**, não como foi pedida.
 *
 * A diferença é o ponto: o servidor limita vida ao teto da vocação e mantém nível e
 * experiência coerentes, então a resposta é o único lugar de onde a tela pode se redesenhar
 * sem mentir.
 */
export function adminSaveStatus(
  token: string,
  characterId: number,
  changes: AdminStatusChanges,
): Promise<{ character: AdminCharacter }> {
  return post('/api/admin/status', { token, characterId, ...changes });
}
